<template>
    <div :class="{breadcrumbs: true, light: light}">
        <template v-for="(item, index) in items" :key="index">
            <span v-if="index === items.length - 1" class="item">{{ item }}</span>
            <NuxtLink v-else-if="index === 0" :to="$withLocalePrefix('/resources')" class="item" external>{{ item }}</NuxtLink>
            <NuxtLink
                v-else
                :to="
                    $withLocalePrefix(
                        `/${pathItems
                            .slice(0, index + 1)
                            .map((pathPart) => breadcrumbItems[pathPart]?.parentPath ?? pathPart)
                            .join('/')}`,
                    )
                "
                class="item"
            >
                {{ item }}
            </NuxtLink>
            <Chevron v-if="index < items.length - 1" class="spacer" />
        </template>
    </div>
</template>
<script setup lang="ts">
    import {ref, inject} from 'vue';
    import {useRoute} from '@typed-router';
    import {useLocalePrefix, useTranslation} from '#imports';
    import {PRISMIC_DOCUMENT} from '~/app/keys';
    import Chevron from '~/components/icons/Chevron.vue';
    import type {TranslationKey} from '#build/i18n';
    const {path} = useRoute();
    const {withoutLocalePrefix} = useLocalePrefix();

    const {t} = useTranslation();
    const document = inject(PRISMIC_DOCUMENT, ref());
    defineProps<{light?: boolean}>();
    const breadcrumbItems: Record<string, {translation: TranslationKey; parentPath?: string; hasContentChild?: boolean}> = {
        resources: {translation: 'resources.resourcesCenter'},
        articles: {translation: 'resources.articles'},
        'success-stories': {translation: 'resources.successStories'},
        'success-story': {translation: 'resources.successStories', parentPath: 'success-stories', hasContentChild: true},
        article: {translation: 'resources.articles', parentPath: 'articles', hasContentChild: true},
        events: {translation: 'resources.events'},
        event: {translation: 'resources.events', parentPath: 'events', hasContentChild: true},
        guides: {translation: 'resources.guides'},
        guide: {translation: 'resources.guides', parentPath: 'guides', hasContentChild: true},
        collection: {translation: 'resources.collections', parentPath: 'collections', hasContentChild: true},
        collections: {translation: 'resources.collections'},
    } as const;
    // first is always empty because path starts with '/'
    // locale prefix (e.g. /fr-fr) is stripped before splitting
    // second and third are to be translated
    // third one is article title or pagination
    const pathItems = withoutLocalePrefix(path).split('/').slice(1);
    const items = pathItems.slice(0, 2).map((item) => t(breadcrumbItems[item].translation));
    if (breadcrumbItems[pathItems[items.length - 1]]?.hasContentChild && document.value?.data?.title) {
        items.push(document.value.data.title);
    }
</script>

<style lang="scss">
    .breadcrumbs {
        display: flex;
        align-items: baseline;
        gap: var(--joy-core-spacing-2);
        color: var(--joy-core-color-neutral-70);

        .item {
            color: var(--joy-core-color-neutral-70);
            white-space: nowrap;

            &:last-child {
                overflow: hidden;
                text-overflow: ellipsis;
            }
        }

        a:hover {
            text-decoration: underline;
        }

        .spacer {
            height: 8px;

            * {
                stroke: var(--joy-color-secondary-50);
            }
        }

        &.light {
            color: var(--joy-color-neutral-0);

            svg * {
                stroke: var(--joy-color-neutral-0);
            }
        }
    }
</style>
